import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import InputField from '../../components/InputField';
import { TextNormalizer } from '../../../../utils/cardHolderNameFieldNormalizer';
import { CardHolderNameValidator } from '../../validator/cardHolderNameFieldValidator';
import { IErrorMessage } from '../../components/errorMessage/errorMessage.type';
import PaymentOutterInput from '../../../../details/paymentMethods/cards/cardForm/payVaultFields/PaymentOutterInput';
import ErrorMessage, { IconType } from '../../components/errorMessage/ErrorMessage';
import { COLORS } from '../../constants/styles';

interface ICardHolderNameFieldProps {
    autoFocus?: boolean;
    intl: InjectedIntl;
    value?: string;
    required?: boolean;
    className?: string;
    region?: string;
}
interface ICardHolderNameFieldStates {
    focus: boolean;
    warningMessage?: string;
}
class CardHolderNameField extends React.Component<
    ICardHolderNameFieldProps,
    ICardHolderNameFieldStates
> {
    private readonly ID: string;
    private readonly LABEL_ID: string;

    private cardHolderNameValidator: CardHolderNameValidator;
    private lastNormalizedValue: string | undefined = undefined;

    constructor(props: ICardHolderNameFieldProps) {
        super(props);

        this.ID = 'ownerName';
        this.LABEL_ID = 'label.owner_name';

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);
        this.updateWarningMessage = this.updateWarningMessage.bind(this);

        this.state = {
            focus: false,
            warningMessage: undefined,
        };

        this.cardHolderNameValidator = new CardHolderNameValidator();
    }

    public componentDidMount() {
        const { value } = this.props;
        if (value) {
            this.updateWarningMessage(value);
        }
    }

    public render() {
        return (
            <Field
                name={this.ID}
                validate={this.validate}
                validateOnChange={false}
                validateOnBlur={true}
                render={this.renderInputField}
            />
        );
    }

    private validate(value: string): IErrorMessage {
        const { required, region } = this.props;

        if (!required && !value?.trim()) {
            return '';
        }

        const result = this.cardHolderNameValidator.validate(value, this.LABEL_ID, region);
        return result !== null ? result.message : '';
    }

    private updateWarningMessage(value: string): void {
        const { intl } = this.props;
        const warningKey = this.cardHolderNameValidator.getWarning(value);
        if (warningKey) {
            this.setState({
                warningMessage: intl.formatMessage({ id: warningKey }),
            });
        } else {
            this.setState({ warningMessage: undefined });
        }
    }

    private renderMessage(
        errorMessage: IErrorMessage | undefined,
        hasWarning: boolean,
        warningMessage: string | undefined,
    ): ReactNode {
        if (errorMessage) {
            return <ErrorMessage message={errorMessage} />;
        }

        if (hasWarning && warningMessage) {
            return <ErrorMessage message={warningMessage} iconType={IconType.INFO} />;
        }

        return null;
    }

    private renderInputField({
        field,
        form: { touched, errors, setFieldValue, handleBlur, setFieldTouched },
    }: FieldProps): ReactNode {
        const { autoFocus, value, required, className } = this.props;
        const { warningMessage } = this.state;
        const isTouched = touched && touched[this.ID];

        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const hasWarning = !!warningMessage && !errorMessage;

        if (value && value !== this.lastNormalizedValue) {
            const normalizedValue = TextNormalizer.trimWhitespace(
                TextNormalizer.normalizeWhitespace(
                    TextNormalizer.normalizeApostrophes(
                        TextNormalizer.toUpperCase(TextNormalizer.fullWidthToHalfWidth(value)),
                    ),
                ),
            );
            if (normalizedValue !== value) {
                this.lastNormalizedValue = normalizedValue;
                setTimeout(() => setFieldValue(this.ID, normalizedValue), 0);
            } else {
                this.lastNormalizedValue = value;
            }
        }

        const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const rawValue = event.target.value;
            this.lastNormalizedValue = rawValue;
            setFieldValue(this.ID, rawValue);
        };

        const handleInputBlur = (event: React.FocusEvent<any>) => {
            this.setState({ focus: false });

            const currentValue = event.target.value;

            const finalValue = TextNormalizer.trimWhitespace(
                TextNormalizer.normalizeWhitespace(
                    TextNormalizer.normalizeApostrophes(
                        TextNormalizer.toUpperCase(
                            TextNormalizer.fullWidthToHalfWidth(currentValue),
                        ),
                    ),
                ),
            );

            this.lastNormalizedValue = finalValue;
            setFieldValue(this.ID, finalValue);

            const validationError = this.validate(finalValue);
            if (!validationError) {
                this.updateWarningMessage(finalValue);
            } else {
                this.setState({ warningMessage: undefined });
            }

            handleBlur(event);
        };
        const handleInputFocus = () => {
            this.setState({
                focus: true,
            });
        };
        const labelClassName =
            !!errorMessage || this.state.focus || hasWarning ? 'form-label-focus' : 'form-label';

        return (
            <div className={className} style={{ position: 'relative' }}>
                <label
                    className={labelClassName}
                    id={this.LABEL_ID}
                    style={{ color: hasWarning ? COLORS.warning : undefined }}
                >
                    {TranslationHelpers.getFormattedMessage(this.LABEL_ID)} *
                </label>
                <PaymentOutterInput
                    showError={!!errorMessage}
                    showWarning={hasWarning}
                    aria-labelledby={this.LABEL_ID}
                    focus={this.state.focus}
                >
                    <InputField
                        autoComplete="cc-name"
                        type="text"
                        id={this.ID}
                        name={this.ID}
                        value={value}
                        autoFocus={autoFocus}
                        required={required}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        hiddenBorder={true}
                        className="wide"
                        onFocus={handleInputFocus}
                        aria-labelledby={this.LABEL_ID}
                    />
                </PaymentOutterInput>
                {this.renderMessage(errorMessage, hasWarning, warningMessage)}
            </div>
        );
    }
}

export default injectIntl(CardHolderNameField);
